import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../api';
import BaseLayout from './BaseLayout';
import FlameLoader from './FlameLoader';
import { getLogoPath } from '../teamUtils';
import { logEvent } from '../apiUtils';
import '../App.css';

// ── Scoring helpers ──────────────────────────────────────────────────────────
/*function scoreMatch(candidate, query) {
  const s = (candidate || '').toLowerCase();
  const q = query.toLowerCase();
  const words = q.split(/\s+/).filter(Boolean);
  if (s === q) return 4;
  if (s.includes(q)) return 3;
  if (words.length > 1 && words.every((w) => s.includes(w))) return 2;
  if (words.some((w) => s.includes(w))) return 1;
  return 0;
}*/

/*function bestScore(fields, query) {
  return Math.max(...fields.map((f) => scoreMatch(f, query)));
}*/

// ── Category section ─────────────────────────────────────────────────────────
const Section = ({ title, icon, children, count }) => (
  <section className="sr-section">
    <h2 className="sr-section-title">
      <i className={`fa ${icon}`}></i> {title}
      <span className="sr-section-count">{count}</span>
    </h2>
    <div className="sr-section-body">{children}</div>
  </section>
);

// ── Sport pill ───────────────────────────────────────────────────────────────
const SportPill = ({ sport }) => (
  <span className="sr-sport-pill">{sport}</span>
);

// ─────────────────────────────────────────────────────────────────────────────
const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = (searchParams.get('q') || '').trim();

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const runSearch = useCallback(async (q) => {
    if (!q) return;
    setLoading(true);
    setError('');
    setResults(null);
    logEvent('used_search');
    try {
      const res = await axios.get(`${API_BASE}/data/search`, { params: { q } });
      setResults(res.data);
    } catch {
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.body.classList.add('search-page-bg');
    runSearch(query);
    return () => document.body.classList.remove('search-page-bg');
  }, [query, runSearch]);

  const totalResults = results
    ? (results.members?.length || 0) +
      (results.games?.length || 0) +
      (results.teams?.length || 0) +
      (results.leagues?.length || 0) +
      (results.groups?.length || 0) +
      (results.achievements?.length || 0)
    : 0;

  return (
    <BaseLayout>
      <div className="sr-container">
        <div className="sr-header">
          <h1 className="sr-title">Search Results</h1>
          {query && (
            <p className="sr-query-label">
              for <span className="sr-query-term">"{query}"</span>
            </p>
          )}
          {!loading && results && (
            <p className="sr-total">{totalResults} result{totalResults !== 1 ? 's' : ''}</p>
          )}
        </div>

        {!query && (
          <p className="sr-empty">Enter a search term in the search bar above.</p>
        )}

        {loading && <FlameLoader text="Searching..." />}
        {error && <p className="error-text">{error}</p>}

        {results && !loading && totalResults === 0 && (
          <div className="sr-no-results">
            <i className="fa fa-search sr-no-results-icon"></i>
            <p>No results found for <strong>"{query}"</strong></p>
            <p className="sr-no-results-hint">Try different keywords or check your spelling.</p>
          </div>
        )}

        {results && (
          <>
            {/* ── Leagues ── */}
            {results.leagues?.length > 0 && (
              <Section title="Leagues" icon="fa-trophy" count={results.leagues.length}>
                <div className="sr-leagues-grid">
                  {results.leagues.map((lg) => (
                    <Link
                      key={lg.id}
                      to={`/league/${lg.id}`}
                      className="sr-league-card"
                    >
                      <img
                        src={getLogoPath(lg.name)}
                        alt={lg.name}
                        className="sr-league-logo"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <span className="sr-league-name">{lg.name}</span>
                      <SportPill sport={lg.sport} />
                    </Link>
                  ))}
                </div>
              </Section>
            )}

            {/* ── Teams ── */}
            {results.teams?.length > 0 && (
              <Section title="Teams" icon="fa-shield" count={results.teams.length}>
                <div className="sr-teams-grid">
                  {results.teams.map((team) => (
                    <Link
                      key={team.id}
                      to={`/team/${team.id}`}
                      className="sr-team-card"
                    >
                      <img
                        src={getLogoPath(team.name)}
                        alt={team.name}
                        className="sr-team-logo"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div className="sr-team-info">
                        <span className="sr-team-name">{team.name}</span>
                        <SportPill sport={team.sport} />
                      </div>
                    </Link>
                  ))}
                </div>
              </Section>
            )}

            {/* ── Members ── */}
            {results.members?.length > 0 && (
              <Section title="Members" icon="fa-users" count={results.members.length}>
                <div className="sr-members-list">
                  {results.members.map((m) => (
                    <Link
                      key={m.username}
                      to={`/user/${m.username}`}
                      className="sr-member-row"
                    >
                      <div className="sr-member-avatar">
                        {m.profile_picture ? (
                          <img src={m.profile_picture} alt={m.username} className="sr-avatar-img" />
                        ) : (
                          <i className="fa fa-user sr-avatar-icon"></i>
                        )}
                      </div>
                      <div className="sr-member-info">
                        <span className="sr-member-name">{m.firstName} {m.lastName}</span>
                        <span className="sr-member-username">@{m.username}</span>
                      </div>
                      <div className="sr-member-stats">
                        <span className="sr-stat-win">{m.wins}W</span>
                        <span className="sr-stat-sep">/</span>
                        <span className="sr-stat-loss">{m.losses}L</span>
                      </div>
                      <i className="fa fa-chevron-right sr-row-arrow"></i>
                    </Link>
                  ))}
                </div>
              </Section>
            )}

            {/* ── Groups ── */}
            {results.groups?.length > 0 && (
              <Section title="Groups" icon="fa-users" count={results.groups.length}>
                <div className="sr-groups-list">
                  {results.groups.map((grp) => (
                    <Link
                      key={grp.id}
                      to={`/groups/${grp.id}`}
                      className="sr-group-row"
                    >
                      <div className="sr-group-avatar">
                        {grp.avatar_url ? (
                          <img src={grp.avatar_url} alt={grp.name} className="sr-avatar-img" />
                        ) : (
                          <i className="fa fa-users sr-avatar-icon"></i>
                        )}
                      </div>
                      <div className="sr-group-info">
                        <span className="sr-group-name">{grp.name}</span>
                        {grp.description && (
                          <span className="sr-group-desc">{grp.description}</span>
                        )}
                      </div>
                      <div className="sr-group-meta">
                        <span className="sr-group-members">
                          <i className="fa fa-user"></i> {grp.member_count}
                        </span>
                        {grp.sport_filter && <SportPill sport={grp.sport_filter} />}
                      </div>
                      <i className="fa fa-chevron-right sr-row-arrow"></i>
                    </Link>
                  ))}
                </div>
              </Section>
            )}

            {/* ── Achievements ── */}
            {results.achievements?.length > 0 && (
              <Section title="Achievements" icon="fa-star" count={results.achievements.length}>
                <div className="sr-achievements-list">
                  {results.achievements.map((ach) => (
                    <Link
                      key={ach.key}
                      to={`/achievements/${ach.key}`}
                      className="sr-achievement-row"
                    >
                      <div className="sr-achievement-icon">
                        {ach.badge_icon ? (
                          <img src={ach.badge_icon} alt={ach.title} className="sr-avatar-img" />
                        ) : (
                          <i className="fa fa-trophy sr-avatar-icon"></i>
                        )}
                      </div>
                      <div className="sr-achievement-info">
                        <span className="sr-achievement-title">{ach.title}</span>
                        <span className="sr-achievement-desc">{ach.description}</span>
                      </div>
                      <div className="sr-achievement-xp">
                        <span className="sr-xp-badge">+{ach.reward_xp} XP</span>
                      </div>
                      <i className="fa fa-chevron-right sr-row-arrow"></i>
                    </Link>
                  ))}
                </div>
              </Section>
            )}

            {/* ── Games ── */}
            {results.games?.length > 0 && (
              <Section title="Games" icon="fa-calendar" count={results.games.length}>
                <div className="sr-games-list">
                  {results.games.map((g) => (
                    <div
                      key={g.game_id}
                      className="sr-game-row"
                      onClick={() => navigate(`/game/${g.game_id}`)}
                    >
                      <div className="sr-game-logos">
                        <img
                          src={getLogoPath(g.home_team)}
                          alt={g.home_team}
                          className="sr-game-logo"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span className="sr-game-vs">vs</span>
                        <img
                          src={getLogoPath(g.away_team)}
                          alt={g.away_team}
                          className="sr-game-logo"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                      <div className="sr-game-info">
                        <span className="sr-game-matchup">{g.home_team} vs {g.away_team}</span>
                        <span className="sr-game-date">{g.date} · {g.time}</span>
                      </div>
                      <SportPill sport={g.sport} />
                      <i className="fa fa-chevron-right sr-row-arrow"></i>
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </>
        )}
      </div>
    </BaseLayout>
  );
};

export default SearchPage;
